// src/components/ai/copilot/AiDraftReviewModal.js
import React, { useEffect, useRef } from 'react';
import AiAttributionBadge from '../AiAttributionBadge';
import AiDraftContextSummary from './AiDraftContextSummary';
import AiGeneratedDraftEditor from './AiGeneratedDraftEditor';

/**
 * The standard two-panel review/edit/approve modal for all AI-generated drafts.
 * Shows what the instructor provided alongside the AI-expanded draft.
 * The instructor edits before accepting — AI output never used directly.
 *
 * Props:
 *   title          — string — modal heading
 *   clientName     — string — shown in subheading
 *   quickInput     — QuickInput object — for AiDraftContextSummary
 *   draftText      — string — current (possibly edited) draft text
 *   originalAiDraft — string — the first AI output, never changes after set
 *   onDraftChange  — (text: string) => void
 *   aiResult       — object | null — for AiAttributionBadge (basedOn, confidence)
 *   editableFields — [{ key, label, value }] — optional structured fields above editor
 *   onEditableFieldChange — (key, value) => void
 *   onAccept       — (finalDraft: string) => void — called on Accept button
 *   onDiscard      — () => void
 *   onRegenerate   — () => void
 *   acceptLabel    — string (default "Accept")
 *   regenerating   — bool
 *   sending        — bool
 *   sendError      — string | null
 *   onClose        — () => void
 */
function AiDraftReviewModal({
  title = 'Review AI Draft',
  clientName = '',
  quickInput = null,
  draftText = '',
  originalAiDraft = '',
  onDraftChange,
  aiResult = null,
  editableFields = [],
  onEditableFieldChange,
  onAccept,
  onDiscard,
  onRegenerate,
  acceptLabel = 'Accept',
  regenerating = false,
  sending = false,
  sendError = null,
  onClose,
}) {
  const hasBeenEdited = draftText !== originalAiDraft && Boolean(originalAiDraft);
  const canAccept = Boolean(draftText.trim()) && !sending && !regenerating;

  // Trap focus inside the modal
  const dialogRef = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    dialogRef.current?.focus();
    return () => previous?.focus();
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/40"
        onClick={onClose || onDiscard}
        aria-hidden="true"
      />

      <div
        ref={dialogRef}
        tabIndex={-1}
        className="relative flex w-full max-w-2xl flex-col rounded-3xl bg-white shadow-xl outline-none"
        style={{ maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-violet-500">{title}</p>
            {clientName ? (
              <h2 className="mt-1 text-lg font-semibold text-slate-950">{clientName}</h2>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose || onDiscard}
            aria-label="Close modal"
            className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* What you provided — always visible */}
          <AiDraftContextSummary quickInput={quickInput} />

          {/* Structured editable fields (e.g. clientName, serviceName, timing) */}
          {editableFields.length > 0 ? (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-slate-500">Review context fields</p>
              {editableFields.map((field) => (
                <div key={field.key}>
                  <label className="mb-1 block text-xs font-medium text-slate-600">{field.label || field.key}</label>
                  <input
                    type="text"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700"
                    value={field.value ?? ''}
                    onChange={(e) => onEditableFieldChange?.(field.key, e.target.value)}
                  />
                </div>
              ))}
            </div>
          ) : null}

          {/* The editable draft */}
          <AiGeneratedDraftEditor
            value={draftText}
            onChange={onDraftChange}
            hasBeenEdited={hasBeenEdited}
            disabled={sending || regenerating}
          />

          {/* Attribution */}
          {aiResult ? (
            <AiAttributionBadge
              basedOn={aiResult.basedOn || []}
              confidence={aiResult.confidence || 'low'}
            />
          ) : null}

          {sendError ? (
            <p className="text-sm text-rose-600">{sendError}</p>
          ) : null}
        </div>

        {/* Footer actions */}
        <div className="flex flex-wrap justify-end gap-3 border-t border-slate-100 px-6 py-4">
          <button
            type="button"
            onClick={onDiscard}
            disabled={sending}
            className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Discard
          </button>
          {onRegenerate ? (
            <button
              type="button"
              onClick={onRegenerate}
              disabled={sending || regenerating}
              className="inline-flex items-center gap-1.5 rounded-full border border-violet-300 px-4 py-2 text-sm font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-50"
            >
              {regenerating ? 'Regenerating…' : 'Regenerate'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => onAccept?.(draftText)}
            disabled={!canAccept}
            className="rounded-full bg-slate-950 px-5 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {sending ? 'Sending…' : acceptLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AiDraftReviewModal;
